import React, { FC, useState } from 'react'
import {
	Visibility,
	Delete,
	Close,
	Done,
	RadioButtonChecked,
	RadioButtonUnchecked,
} from '@mui/icons-material'
import { DocumentVersion } from '../../../../../lib/api/types/document.interfaces'
import { useUserDateFormat } from '../../../../../hooks/useUserDateFormat'
import { IconButton, OptionsMenuButton, Tooltip } from '../../../../ui'
import cn from '../../../../../lib/utils/cn'
import { useDeleteObjectVersion } from '../../../../../hooks/api/documentHooks'
import { useMediaQuery } from 'react-responsive'
import { Features, useBilling } from '../../../../../context/billingContext'

interface FileVersionsTableProps {
	versions: DocumentVersion[]
	selectedVersionId?: string
	currentVersionId?: string
	onSelectVersion: React.Dispatch<React.SetStateAction<string | null>>
	onPreview: (versionId: string) => void
	currentObjectId?: string
	originalVersionBeforeSigningId?: string | null
}

const FileVersionsTable: FC<FileVersionsTableProps> = ({
	versions,
	selectedVersionId,
	currentVersionId,
	onSelectVersion,
	onPreview,
	currentObjectId,
	originalVersionBeforeSigningId,
}) => {
	const { formatDate } = useUserDateFormat()
	const { hasFeature } = useBilling()
	const isMobile = useMediaQuery({ maxWidth: 767 })
	const [deletingVersionId, setDeletingVersionId] = useState<string | null>(
		null,
	)

	const { mutate: deleteVersion, isPending: isDeleting } =
		useDeleteObjectVersion()

	const handleDelete = () => {
		const deletingVersion = versions?.find(
			(version) => version?.id === deletingVersionId,
		)
		const canDeleteVersion =
			Number(versions?.length) > 1 &&
			Boolean(deletingVersion) &&
			deletingVersionId !== currentVersionId &&
			!deletingVersion?.currentVersion &&
			!deletingVersion?.isSigned &&
			deletingVersion?.id !== originalVersionBeforeSigningId

		if (currentObjectId && deletingVersionId && canDeleteVersion) {
			deleteVersion(
				{
					id: currentObjectId,
					versionId: deletingVersionId,
				},
				{
					onSuccess: () => {
						setDeletingVersionId(null)
						onSelectVersion((selected) =>
							selected === deletingVersionId
								? (currentVersionId ?? null)
								: selected,
						)
					},
				},
			)
		}
	}

	return (
		<div className="overflow-y-auto min-h-[100px] max-h-[calc(100vh-100px)] md:max-h-[230px] border-t border-stroke-light dark:border-stroke-light-dark ">
			{isMobile ? (
				<div>
					{versions?.map(
						({
							id,
							name,
							versionNumber,
							isSigned,
							currentVersion: isCurrentVersion,
						}) => {
							const isOriginalVersionBeforeSigning =
								id === originalVersionBeforeSigningId
							const canDeleteVersion =
								Number(versions?.length) > 1 &&
								id !== currentVersionId &&
								!isCurrentVersion &&
								!isSigned &&
								!isOriginalVersionBeforeSigning
							return (
								<div
									key={id}
									className="h-12 w-full px-5 border-b border-stroke-light dark:border-stroke-light-dark  flex items-center justify-start box-border"
								>
									<div className="flex items-center justify-start gap-1 w-3/4">
										<span className="text-stroke-dark dark:text-text-table-dark shrink-0">
											[v{versionNumber}]
										</span>
										<p className="text-text-table dark:text-text-table-dark truncate max-w-2/3">
											{name}
										</p>{' '}
										{(isCurrentVersion || id === currentVersionId) && (
											<span className="w-fit h-4 flex items-center justify-center text-sm px-0.5 rounded bg-accent text-white ml-3 shrink-0">
												Current
											</span>
										)}
									</div>

									<OptionsMenuButton
										className="ml-auto text-xs"
										items={[
											{
												icon: <Visibility className="fill-text-secondary" />,
												label: 'Preview',
												onClick: () => onPreview(id),
											},
											{
												icon: (
													<RadioButtonChecked className="fill-text-secondary" />
												),
												label: 'Make current',
												onClick: () => onSelectVersion(id),
											},
											{
												icon: <Delete className="fill-text-secondary" />,
												label: 'Delete',
												onClick: () => setDeletingVersionId(id),
												disabled: !canDeleteVersion,
											},
										]}
									/>
								</div>
							)
						},
					)}
				</div>
			) : (
				<table className="w-full border-collapse">
					<thead className="sticky z-10 top-0 bg-white dark:bg-background-dark border-b border-stroke-light dark:border-stroke-light-dark ">
						<tr className="bg-background-light dark:bg-background-light-dark text-sm font-semibold text-text-table dark:text-text-table-dark h-10">
							<th className="px-2 text-left text-sm w-[54px]">Current</th>
							<th className="pl-5 text-left">Version</th>
							<th className="pl-5 text-left hidden lg:table-cell">
								Created By
							</th>
							<th className="pl-5 text-left hidden lg:table-cell">
								Creation Date
							</th>

							<th className="px-0 w-[88px]"></th>
						</tr>
					</thead>
					<tbody className="overflow-hidden">
						{versions?.map(
							({
								id,
								createdAt,
								createdBy,
								versionNumber,
								isSigned,
								currentVersion: isCurrentVersion,
							}) => {
								const isSelected = selectedVersionId === id
								const isOriginalVersionBeforeSigning =
									id === originalVersionBeforeSigningId
								const canDeleteVersion =
									Number(versions?.length) > 1 &&
									id !== currentVersionId &&
									!isCurrentVersion &&
									!isSigned &&
									!isOriginalVersionBeforeSigning
								return (
									<tr
										key={id}
										className={cn(
											'border-b border-stroke-light dark:border-stroke-light-dark  text-sm text-text-table dark:text-text-table-dark cursor-pointer',
											isSelected
												? 'bg-accent-light dark:bg-accent/30'
												: 'bg-white dark:bg-background-dark',
										)}
										onClick={() => onSelectVersion(id)}
									>
										<td className="px-2 h-12">
											<button
												className={cn(
													'w-9 h-[34px] rounded-md flex items-center justify-center mx-auto',
													isSelected
														? 'bg-accent'
														: 'bg-white dark:bg-background-dark border border-stroke-light dark:border-stroke-light-dark ',
												)}
												onClick={(e) => {
													e.stopPropagation()
													onSelectVersion(id)
												}}
											>
												{isSelected ? (
													<RadioButtonChecked className="fill-white" />
												) : (
													<RadioButtonUnchecked className="fill-black dark:fill-stroke-dark" />
												)}
											</button>
										</td>
										<td className="px-5 text-left h-12 max-w-[184px] box-border">
											{isSigned ? (
												<Tooltip label="This version of the document is the final version as it is signed by the members.">
													<div className="text-xs bg-accent text-white rounded w-fit px-2 h-5 flex items-center justify-center">
														SIGNED
													</div>
												</Tooltip>
											) : (
												versionNumber
											)}
										</td>
										<td className="pl-5 h-12 hidden lg:table-cell">
											{createdBy?.id
												? createdBy?.name || createdBy?.email
												: '-'}
										</td>
										<td className="pl-5 h-12 hidden lg:table-cell">
											{createdAt ? formatDate(createdAt) : '-'}
										</td>
										<td className="px-1 h-12 relative">
											<div className="flex justify-end gap-2">
												{hasFeature(Features.Basic) && (
													<IconButton
														variant="transparent"
														icon={<Visibility className="fill-inherit" />}
														onClick={() => onPreview(id)}
														className={cn(
															'ring-0 bg-white dark:bg-background-dark',
															isSelected
																? 'fill-accent shadow-sm hover:bg-white dark:bg-background-light-dark dark:hover:bg-background-dark hover:fill-accent-hover'
																: 'fill-text-secondary hover:bg-white dark:bg-background-light-dark dark:hover:bg-background-dark hover:fill-text-main',
														)}
														tooltip="Preview"
														tooltipOffset={isSelected ? 2 : -2}
													/>
												)}
												<IconButton
													variant="transparent"
													icon={<Delete className="fill-inherit" />}
													onClick={() => setDeletingVersionId(id)}
													className={cn(
														'ring-0 bg-white dark:bg-background-dark',
														isSelected
															? 'fill-accent shadow-sm hover:bg-white dark:bg-background-light-dark dark:hover:bg-background-dark hover:fill-accent-hover'
															: 'fill-text-secondary hover:bg-white dark:bg-background-light-dark dark:hover:bg-background-dark hover:fill-text-main',
													)}
													tooltip={
														isCurrentVersion || id === currentVersionId
															? "You can't delete the current version"
															: isSigned
																? "You can't delete signed document version"
																: Number(versions?.length) < 2
																	? "You can't delete a single version of this file"
																	: isOriginalVersionBeforeSigning
																		? "You can't delete the original version that existed before signing"
																		: 'Delete'
													}
													tooltipOffset={isSelected ? 2 : -2}
													disabled={!canDeleteVersion}
												/>
											</div>
											<div
												className={cn(
													'bg-white dark:bg-background-dark items-center gap-2 absolute z-10 hidden pl-4 pr-1.5 inset-y-0.5 right-1 rounded-lg border border-stroke-light dark:border-stroke-light-dark  shadow-sm',
													deletingVersionId === id && 'flex h-11',
												)}
											>
												<span className="mr-1 font-semibold text-sm text-text-main dark:text-text-main-dark">
													Delete?
												</span>
												<IconButton
													variant="secondary"
													className="h-8 w-8"
													icon={
														<Close className="fill-black dark:fill-stroke-dark" />
													}
													onClick={() => setDeletingVersionId(null)}
												/>
												<IconButton
													className="h-8 w-8"
													icon={<Done className="fill-white" />}
													onClick={handleDelete}
													loading={isDeleting}
													disabled={!canDeleteVersion}
												/>
											</div>
										</td>
									</tr>
								)
							},
						)}
					</tbody>
				</table>
			)}
		</div>
	)
}

export default FileVersionsTable
